import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  chatbotHitlMessage,
  chatbotUserMessage,
  endConversation,
  getChatbotHitlMessage,
  getChatRoomStatus,
} from "../api/ChatBotApiCall";
import {
  AdminResponse,
  ChatRoomStatus,
  MessageStatus,
} from "../pages/chatbot/dataModel/ChatBotDataModel";
import { AppDispatch } from "./common/store";
import { showConsoleMessage } from "../util/ConsoleMessage";
import { HitlMessage } from "../api/responsePayload/HitlMessageResponse";

interface onChatbotState {
  chatRoomStatus: ChatRoomStatus;
  adminResponse: AdminResponse;
  customerSentEvent: AdminResponse;

  isUpdatingSmartContext: boolean;

  aiMessageList: HitlMessage[];
  isEndConversation: boolean;
}

const initialState: onChatbotState = {
  chatRoomStatus: ChatRoomStatus.DEFAULT,
  customerSentEvent: {
    memberId: null,
    message: null,
    messageId: null,
    messageStatus: null,
  },
  adminResponse: {
    memberId: null,
    message: null,
    messageId: null,
    messageStatus: null,
  },
  isUpdatingSmartContext: false,

  aiMessageList: [],
  isEndConversation: false,
};

export const onChatbotSlice = createSlice({
  name: "on-Chatbot",
  initialState: initialState,
  reducers: {
    setChatRoomStatus(state, action: PayloadAction<ChatRoomStatus>) {
      state.chatRoomStatus = action.payload;
    },

    setCustomerSentEvent(state, action: PayloadAction<AdminResponse>) {
      state.customerSentEvent = action.payload;
    },

    setAdminResponse(state, action: PayloadAction<AdminResponse>) {
      state.adminResponse = action.payload;
    },

    setAiMessageList(state, action: PayloadAction<HitlMessage[]>) {
      state.aiMessageList = action.payload;
    },

    setUpdatingSmartContext(state, action: PayloadAction<boolean>) {
      state.isUpdatingSmartContext = action.payload;
    },

    updateAiMessageStatus(
      state,
      action: PayloadAction<{ messageId: string; newStatus: number }>
    ) {
      const { messageId, newStatus } = action.payload;
      const message = state.aiMessageList.find(
        (msg) => msg.message_id === messageId
      );
      if (message) {
        message.status = newStatus;
      }
    },

    setEndConversation(state, action: PayloadAction<boolean>) {
      state.isEndConversation = action.payload;
    },

    resetChatbotState: () => initialState,
  },
});

export const getUserMessageApiCall =
  (message: string, threadId: string, memberId: number, teamId: number) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      await chatbotUserMessage(message, threadId, memberId, teamId);

      // Start polling in background (do not await)
      pollChatRoomStatusUntilComplete(memberId, dispatch);
    } catch (error) {
      console.error("Failed to send user message", error);
    }
  };

export const postHitlMessageApiCall =
  (
    message: string,
    memberId: number,
    messageStatus: MessageStatus,
    messageId?: string,
    image_urls?: string[]
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      const adminResponse: AdminResponse = {
        message: message,
        memberId: memberId,
        messageStatus: messageStatus,
        messageId: messageId,
        image_urls: image_urls,
      };
      if (messageStatus != MessageStatus.Rejected)
        dispatch(onChatbotActions.setCustomerSentEvent(adminResponse));

      await chatbotHitlMessage(
        message,
        memberId,
        messageStatus,
        messageId,
        image_urls
      );

      if (messageStatus != MessageStatus.Rejected)
        dispatch(onChatbotActions.setAdminResponse(adminResponse));

      showConsoleMessage("----admin Response----API", adminResponse);
    } catch (error) {
      console.error("Failed to save admin response", error);
      return error;
    }
  };

export const getHitlMessageApiCall =
  (memberId: number, current?: boolean) => async (dispatch: AppDispatch) => {
    try {
      const res = await getChatbotHitlMessage(memberId, current);
      if (res != undefined)
        dispatch(
          onChatbotActions.setAiMessageList(
            res.results.filter((it) => it.status != 4)
          )
        );
    } catch (error) {
      console.error("Failed to save getHitlMessageApiCall response", error);
      return error;
    }
  };

export const getChatRoomStatusApiCall =
  (chatId: number) =>
  async (dispatch: AppDispatch): Promise<ChatRoomStatus> => {
    try {
      const response = await getChatRoomStatus(chatId);

      const status = response?.status;
      const mappedStatus =
        status === 1
          ? ChatRoomStatus.WAITING
          : status === 2
          ? ChatRoomStatus.PROCESSING
          : status === 3
          ? ChatRoomStatus.COMPLETE
          : ChatRoomStatus.DEFAULT;

      dispatch(onChatbotActions.setChatRoomStatus(mappedStatus));
      return mappedStatus;
    } catch (error) {
      console.error("Failed to fetch chat room status", error);
      return ChatRoomStatus.DEFAULT;
    }
  };

export const pollChatRoomStatusUntilComplete = async (
  memberId: number,
  dispatch: AppDispatch,
  options?: {
    intervalMs?: number; // polling interval
    timeoutMs?: number; // optional timeout to prevent infinite loop
  }
): Promise<ChatRoomStatus> => {
  dispatch(onChatbotActions.setUpdatingSmartContext(true)); // ✅
  const interval = options?.intervalMs ?? 6000;
  const timeout = options?.timeoutMs ?? 100000;

  return new Promise<ChatRoomStatus>((resolve, reject) => {
    const startTime = Date.now();

    const checkStatus = async () => {
      const status = await dispatch(getChatRoomStatusApiCall(memberId));

      if (status === ChatRoomStatus.COMPLETE) {
        await dispatch(getHitlMessageApiCall(memberId, true));
        dispatch(onChatbotActions.setUpdatingSmartContext(false)); // ✅
        return resolve(ChatRoomStatus.COMPLETE);
      }

      if (Date.now() - startTime > timeout) {
        return reject(
          new Error("Timeout while waiting for status to complete.")
        );
      }

      setTimeout(checkStatus, interval);
    };

    checkStatus();
  });
};

export const endConversationApiCall =
  (memberId: number) => async (dispatch: AppDispatch) => {
    await endConversation(memberId);
    dispatch(onChatbotActions.resetChatbotState());
  };

export const onChatbotActions = onChatbotSlice.actions;

export default onChatbotSlice.reducer;
